import { z } from 'zod';

export const ApplyPaymentSchema = z
  .object({
    paymentMode: z.string({ message: 'Payment Mode is required' }),

    paymentDate: z.date().nullish(),

    paymentAmount: z.string().nullish(),

    balance: z.string().nullish(),

    invoiceNumbers: z.array(z.string()).nullish(),

    memo: z.string().nullish(),

    checkNumber: z.string().nullish(),

    lastInvoiceNumber: z.string().nullish(),
  })
  .superRefine((data, ctx) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const isCheckMode = data.paymentMode === 'CHECK' || data.paymentMode === 'E_CHECK';

    if (!isCheckMode) {
      if (!data.paymentDate) {
        ctx.addIssue({
          code: 'custom',
          message: 'Payment Date is required',
          path: ['paymentDate'],
        });
      } else if (data.paymentDate > today) {
        ctx.addIssue({
          code: 'custom',
          message: 'Payment date cannot be in the future',
          path: ['paymentDate'],
        });
      }
    }

    if (data.paymentAmount === null || data.paymentAmount === undefined || data.paymentAmount === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'Amount is required',
        path: ['paymentAmount'],
      });
    } else {
      const amount = parseFloat(data.paymentAmount);
      if (isNaN(amount) || amount < 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Amount cannot be negative',
          path: ['paymentAmount'],
        });
      } else if (amount > Number.MAX_SAFE_INTEGER) {
        ctx.addIssue({
          code: 'custom',
          message: 'Invalid Amount ',
          path: ['paymentAmount'],
        });
      }
    }

    if (!data.invoiceNumbers || data.invoiceNumbers.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one invoice is required',
        path: ['invoiceNumbers'],
      });
    }

    if (isCheckMode && !data.checkNumber) {
      ctx.addIssue({
        code: 'custom',
        message: 'Check number is required for Check and E-check payment modes',
        path: ['checkNumber'],
      });
    }

    const balance = parseFloat(data.balance ?? '0');
    if (!isNaN(balance) && balance !== 0 && !data.memo) {
      ctx.addIssue({
        code: 'custom',
        message: 'Memo is required when there is a balance',
        path: ['memo'],
      });
    }

    if (data.lastInvoiceNumber && data.invoiceNumbers && !data.invoiceNumbers.includes(data.lastInvoiceNumber)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Last invoice number must be one of the selected invoices',
        path: ['lastInvoiceNumber'],
      });
    }

    if (
      data.invoiceNumbers &&
      data.invoiceNumbers.length > 1 &&
      !isNaN(balance) &&
      balance !== 0 &&
      !data.lastInvoiceNumber
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Last invoice number is required when applying payment to multiple invoices with balance',
        path: ['lastInvoiceNumber'],
      });
    }
  });

export type ApplyPaymentFormData = z.infer<typeof ApplyPaymentSchema>;
